import {
    Component,
    ElementRef,
    ViewChild,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    Renderer2
} from '@angular/core';
import { Gesture, GestureController, DomController } from '@ionic/angular';

const PICKER_ITEM_SELECTED = 'picker-item-selected';

@Component({
    selector: 'haleo-picker-column',
    templateUrl: 'picker-column.component.html',
    styleUrls: ['picker-column.component.scss']
})
export class PickerColumnComponent implements OnChanges {

    @ViewChild('el') el: ElementRef;
    @Input() col: { options: { text: string; value: string | number, duration?: any, transform?: any }[]; columnWidth: number; selectedIndex: number };
    @Output() onChange: EventEmitter<any>;

    private optionHeight = 30;
    private y = 0;
    private optsEl: any = [];
    private gesture: Gesture;
    private noAnimate = true;
    private latestY = 0;
    private upperLimit: number;
    private lowerLimit: number;
    private boxHeight = 160;
    private midHeight = 0;

    constructor(private gestureController: GestureController,
                private elementRef: ElementRef,
                private domCtrl: DomController,
                private renderer: Renderer2) {
        this.onChange = new EventEmitter<any>();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes.col.firstChange) {
            this.ngAfterViewInit();
        }
    }

    ngAfterViewInit() {
        this.render();
    }

    private async render() {

        const childElements = this.el.nativeElement.childNodes;
        for (let child of childElements) {
            this.renderer.removeChild(this.el.nativeElement, child);
        }
        this.optsEl = [];

        const children: any = [];

        if (!this.col) {
            return;
        }

        await this.domCtrl.read(() => {
            // there is 3.5 px offset between child element
            // 65 is the relative (160 / 2) - child height (30 / 2)
            this.midHeight = 65 - ((this.col.selectedIndex) * (this.optionHeight));
            const selectedIndex = this.col.selectedIndex;

            for (let i = 0; i < this.col.options.length; i++) {

                const button = this.renderer.createElement('button');
                const span = this.renderer.createElement('span');
                this.renderer.addClass(button, 'picker-item');
                this.renderer.addClass(span, 'ion-text-wrap');

                let height;
                if (i < this.col.selectedIndex) {
                    height = this.midHeight - (this.optionHeight * (selectedIndex - i));

                } else if (i > this.col.selectedIndex) {
                    height = this.midHeight + (this.optionHeight * (i - selectedIndex));
                } else {
                    height = this.midHeight;
                    this.renderer.addClass(button, PICKER_ITEM_SELECTED);
                }

                this.renderer.setStyle(button, 'top', this.midHeight + 'px');
                this.renderer.setStyle(button, 'height', this.optionHeight + 'px');

                const text = this.renderer.createText(this.col.options[i].text);
                this.renderer.appendChild(span, text);
                this.renderer.appendChild(button, span);

                children.push({
                    index: i,
                    element: button
                });
            }

            children.forEach((op: any) => {
                this.renderer.appendChild(this.el.nativeElement, op.element);
                this.optsEl.push(op.element);
            });

            if (!this.col || !this.optsEl || !this.el.nativeElement) {
                return;
            }

            this.gesture = this.gestureController.create({
                el: this.el.nativeElement,
                gestureName: 'picker-swipe',
                gesturePriority: 100,
                threshold: 0,
                passive: false,
                onStart: detail => this.onStart(detail),
                onMove: detail => this.onMove(detail),
                onEnd: () => this.onEnd()
            });

            this.gesture.enable();
        });
    }

    private findDimensions() {

        const {top} = this.el.nativeElement.getBoundingClientRect();
        const halfHeight = (this.boxHeight / 2);
        this.upperLimit = top + halfHeight - (this.optionHeight / 2);
        this.lowerLimit = this.upperLimit + this.optionHeight;
    }

    private isCentre(top: number, bottom: number) {
        return Math.abs(top - this.upperLimit) <= 10 || Math.abs(bottom - this.lowerLimit) <= 10;
    }

    private update(y: number, duration: number, saveY: boolean, adjust: boolean = false, index = -1, refresh = false) {

        let translateY = 0;
        let translateZ = 0;

        const durationStr = (duration === 0) ? '' : duration + 'ms';

        const children = this.optsEl;
        for (let i = 0; i < children.length; i++) {
            const button = children[i] as HTMLElement;
            const opt = this.col.options[i];

            const optOffset = y;
            let transform = '';

            translateZ = 0;
            translateY = optOffset;

            transform += `translate3d(0px,${translateY}px,${translateZ}px) `;

            // Update transition duration
            if (this.noAnimate) {
                opt.duration = 0;
                button.style.transitionDuration = '';

            } else if (duration !== opt?.duration) {
                opt.duration = duration;
                button.style.transitionDuration = durationStr;
            }

            // Update transform
            if (transform !== opt?.transform) {
                opt.transform = transform;
                button.style.transform = transform;
            }

            const {top, bottom} = children[i].getBoundingClientRect();

            // Don't apply the style while adjusting the selected index
            if (!adjust) {

                if (this.isCentre(top, bottom)) {
                    this.col.selectedIndex = i;
                    button.classList.add(PICKER_ITEM_SELECTED);
                } else {
                    button.classList.remove(PICKER_ITEM_SELECTED);
                }
            }

            if (refresh && this.isCentre(top, bottom) && i == index) {
                this.latestY = y;
            }
        }

        if (saveY) {
            this.y = y;
        }
    }

    private onStart(detail: any) {

        if (detail.event.cancelable) {
            detail.event.preventDefault();
        }
        detail.event.stopPropagation();

        // need to update the dimensions again
        this.findDimensions();
    }

    private onMove(detail: any) {
        if (detail.event.cancelable) {
            detail.event.preventDefault();
        }
        detail.event.stopPropagation();

        this.latestY = detail.deltaY + this.y;
        this.update(this.latestY, 0, false, false, -1, false);
    }

    private adjustSelected(selectedIndex: number) {

        const selectedTop = this.optsEl[selectedIndex].getBoundingClientRect().top;
        const {top, bottom} = this.el.nativeElement.getBoundingClientRect();
        const halfHeight = top + ((bottom - top) / 2);
        const offset = this.latestY + (halfHeight - selectedTop) - 15;

        this.update(offset, 100, true, true, -1, false);
    }

    private onEnd() {
        this.onChange.emit(this.col.options[this.col.selectedIndex].value);

        const selected = this.optsEl[this.col.selectedIndex] as HTMLElement;
        selected.classList.add(PICKER_ITEM_SELECTED);
        this.adjustSelected(this.col.selectedIndex);
    }
}
